import { useState, useEffect, useRef } from "react";
import { Instagram, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import ScrollReveal from "./ScrollReveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
// Tło sekcji kontaktu — pełna szerokość, opacity 0.7 + overlay.
import heroPhoto3 from "@/assets/hero-photo-3.jpg";

const ContactSection = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const imgRef = useRef<HTMLImageElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!imgRef.current || !sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const y = -rect.top * 0.2;
      imgRef.current.style.transform = `translateY(${y}px)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const submissionId = crypto.randomUUID();
    try {
      const [notify, confirm] = await Promise.all([
        supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "contact-notification",
            recipientEmail: "michalwencek@gmail.com",
            idempotencyKey: `contact-notify-${submissionId}`,
            templateData: {
              name: formData.name,
              email: formData.email,
              message: formData.message,
            },
          },
        }),
        supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "contact-confirmation",
            recipientEmail: formData.email,
            idempotencyKey: `contact-confirm-${submissionId}`,
            templateData: { name: formData.name, message: formData.message },
          },
        }),
      ]);
      if (notify.error) throw notify.error;
      if (confirm.error) throw confirm.error;
      toast({ title: t.contact.toastTitle, description: t.contact.toastDescription });
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("Contact form send failed", err);
      toast({
        title: "Błąd wysyłki",
        description: "Nie udało się wysłać wiadomości. Spróbuj ponownie lub napisz bezpośrednio na michalwencek@gmail.com.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={sectionRef} id="contact" className="relative py-10 md:py-16 px-6 overflow-hidden">
      {/* Background photo with parallax */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          ref={imgRef}
          src={heroPhoto3}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-70 will-change-transform"
        />
        <div className="absolute inset-0 bg-background/40" />
      </div>
      <div className="max-w-4xl mx-auto relative z-10">
        <ScrollReveal>
          <span className="block text-center text-accent text-xs font-mono tracking-widest uppercase">
            [ {t.contact.label} // 04 ]
          </span>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <h2 className="mt-4 text-center text-3xl md:text-4xl lg:text-5xl font-bold text-foreground uppercase glitch cursor-default">
            {t.contact.heading}
          </h2>
        </ScrollReveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-16">
          <ScrollReveal delay={200}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Input
                  placeholder={t.contact.namePlaceholder}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-background/80 backdrop-blur-sm"
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder={t.contact.emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-background/80 backdrop-blur-sm"
                />
              </div>
              <div>
                <Textarea
                  placeholder={t.contact.messagePlaceholder}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="bg-background/80 backdrop-blur-sm resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                {isSubmitting ? (
                  t.contact.sending
                ) : (
                  <>
                    {t.contact.sendButton}
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </ScrollReveal>

          <ScrollReveal delay={300}>
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 drop-shadow-[0_2px_12px_rgba(255,255,255,0.8)]">
                  {t.contact.getInTouch}
                </h3>
                <p className="text-foreground/90 leading-relaxed whitespace-pre-line drop-shadow-[0_2px_12px_rgba(255,255,255,0.8)]">
                  {t.contact.getInTouchText}
                </p>
              </div>

              <div className="space-y-4">
                <a
                  href="mailto:michalwencek@gmail.com"
                  className="flex items-center gap-3 text-foreground hover:text-accent transition-colors group"
                >
                  <Mail className="w-5 h-5" />
                  <span className="relative">
                    michalwencek@gmail.com
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-accent transition-all group-hover:w-full" />
                  </span>
                </a>

                <a
                  href="https://instagram.com/wencek_michal"
                  className="flex items-center gap-3 text-foreground hover:text-accent transition-colors group"
                >
                  <Instagram className="w-5 h-5" />
                  <span className="relative">
                    @wencek_michal
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-accent transition-all group-hover:w-full" />
                  </span>
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={400}>
          <div className="mt-24 pt-12 border-t border-border text-center">
            <p className="text-sm text-muted-foreground tracking-widest uppercase">
              {t.contact.footer}
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              © {new Date().getFullYear()} {t.contact.copyright}
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ContactSection;
